import { Intent } from "@blueprintjs/core";
import { showNotification } from "../store/toasterDuck";
import { fetchWithAuth, safeJson } from "../services/helpers/api";
import { getColumnDefinitions } from "../services/helpers/headers";
import type { InputOrbitRow } from "../services/inputs";
import type { OrbitFileStatus } from "../types/orbits";
import {
  COLUMNDEFS_INPUTS_OPS,
  ORBIT_STATUS_DISPLAY,
  ORBIT_STATUS_FILTER_VALUES,
} from "./inputsConfig";
export {
  getComputedInputState,
  InputStateBadge,
  inputStatePillClassName,
  inputStateSelectedClassName,
} from "./inputState";

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const FILTERABLE_HEADERS = new Set(["status", "organization"]);
export const RESCREENABLE_STATUSES = new Set<OrbitFileStatus>([
  "ERROR",
  "REJECTED",
]);

export const ACTION_ICON_CLASS_NAME =
  "p-1.5 rounded-md hover:bg-gray-300/50 text-black transition-colors duration-150 cursor-pointer";
export const DISABLED_ACTION_ICON_CLASS_NAME =
  "p-1.5 rounded-md text-black disabled-icon-link";
export const STATUS_BUTTON_CLASS_NAME =
  "flex border-0 bg-transparent p-1.5 rounded-md hover:bg-gray-300/50 transition-colors duration-150 cursor-pointer";

export const LINKED_OEM_ACTION_CONFIG = {
  info: {
    icon: "eye-open",
    noOrbitTitle: "No orbit available",
  },
  download: {
    icon: "download",
    noOrbitTitle: "No orbit available",
  },
  plots: {
    icon: "timeline-line-chart",
    noOrbitTitle: "No orbit available",
  },
} as const;

export const setInputFilePending = async (
  payload: { id: string },
  dispatch: (action: ReturnType<typeof showNotification>) => void,
  onSuccess?: () => void,
) => {
  if (!payload || !payload.id) return;

  const url = `/api/v1/inputs/${encodeURIComponent(payload.id)}/status`;

  try {
    const res = await fetchWithAuth(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "PENDING" }),
    });

    await safeJson(res);
    onSuccess?.();
  } catch (err) {
    dispatch(
      showNotification({
        intent: Intent.DANGER,
        message: `Unable to set input file ${payload.id} to PENDING`,
      }),
    );
    console.warn(`Unable to set input file ${payload.id} to PENDING`, err);
    throw err;
  }
};

export const isOrbitFileStatus = (status: string): status is OrbitFileStatus =>
  status in ORBIT_STATUS_DISPLAY;

export const getOrbitStatus = (rowData: InputOrbitRow): OrbitFileStatus => {
  const status = rowData.status;
  const rawStatus =
    typeof status === "object" && status !== null ? status.value : status;
  const normalizedStatus = String(rawStatus ?? "-").toUpperCase();

  return isOrbitFileStatus(normalizedStatus) ? normalizedStatus : "-";
};

export const getOrbitStatusDisplay = (rowData: InputOrbitRow) =>
  ORBIT_STATUS_DISPLAY[getOrbitStatus(rowData)];

export const canSetInputFilePending = (rowData: InputOrbitRow) =>
  RESCREENABLE_STATUSES.has(getOrbitStatus(rowData));

export const getInputsColumnDefinitions = () =>
  getColumnDefinitions(
    COLUMNDEFS_INPUTS_OPS as unknown as Parameters<
      typeof getColumnDefinitions
    >[0],
  ).map((column) => ({
    ...column,
    enableColumnFilter: FILTERABLE_HEADERS.has(column.key),
    ...(column.key === "status" && {
      customFilterValues: ORBIT_STATUS_FILTER_VALUES,
      filterFn: "equalsString" as const,
    }),
  }));

export const getLogSeverityClassName = (severity: string) => {
  switch (severity.toUpperCase()) {
    case "INFO":
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    case "WARN":
    case "WARNING":
      return "bg-yellow-50 text-yellow-800 ring-yellow-600/20";
    case "ERROR":
      return "bg-red-50 text-red-700 ring-red-600/20";
    default:
      return "bg-gray-50 text-gray-700 ring-gray-600/20";
  }
};

export const getEndDateTime = (linkedOEM: any): number | null => {
  const value = linkedOEM?.endDate?.value;

  if (!value) {
    return null;
  }

  const time = new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

export const isExpired = (linkedOEM: any, now = Date.now()): boolean => {
  const endDateTime = getEndDateTime(linkedOEM);

  return endDateTime !== null && endDateTime < now;
};

export const isCloseToExpired = (linkedOEM: any, now = Date.now()): boolean => {
  const endDateTime = getEndDateTime(linkedOEM);

  return (
    endDateTime !== null && endDateTime >= now && endDateTime < now + ONE_DAY_MS
  );
};

